import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaFolder, FaSort, FaEye } from 'react-icons/fa';
import { subcategoriesAPI, categoriesAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Subcategories = () => {
    const [subcategories, setSubcategories] = useState([]);
    const [filteredSubcategories, setFilteredSubcategories] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        category_id: '',
        is_active: '1'
    });

    useEffect(() => {
        fetchCategories();
        fetchSubcategories();
    }, [pagination.page, selectedCategory]);

    useEffect(() => {
        if (Array.isArray(subcategories)) {
            const filtered = subcategories.filter(subcategory => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    subcategory.name?.toLowerCase().includes(searchLower) ||
                    subcategory.description?.toLowerCase().includes(searchLower) ||
                    subcategory.slug?.toLowerCase().includes(searchLower)
                );
            });
            setFilteredSubcategories(filtered);
        }
    }, [searchTerm, subcategories]);

    const fetchCategories = async () => {
        try {
            setCategoriesLoading(true);
            const response = await categoriesAPI.list({ page: 1, limit: 100 });

            let categoriesData = [];

            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    categoriesData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    categoriesData = response.data.data;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                categoriesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                categoriesData = response.data;
            }

            setCategories(categoriesData);
        } catch (err) {
            console.error('Error fetching categories:', err);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const fetchSubcategories = async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page: pagination.page,
                limit: pagination.limit
            };

            if (selectedCategory) {
                params.category_id = selectedCategory;
            }

            const response = await subcategoriesAPI.list(params);

            console.log('Subcategories API Response:', response.data);

            let subcategoriesData = [];
            let paginationData = {};

            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    subcategoriesData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    subcategoriesData = response.data.data;
                }
                if (response.data.data.meta) {
                    paginationData = response.data.data.meta;
                }
            }

            if (!Array.isArray(subcategoriesData)) {
                console.error('Invalid subcategories data format:', response.data);
                subcategoriesData = [];
            }

            setSubcategories(subcategoriesData);
            setFilteredSubcategories(subcategoriesData);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || subcategoriesData.length,
                pages: paginationData.pages || Math.ceil((paginationData.total || subcategoriesData.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching subcategories:', err);
            setError(err.response?.data?.message || 'Failed to fetch subcategories');
            setSubcategories([]);
            setFilteredSubcategories([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            if (!formData.name || !formData.slug || !formData.category_id) {
                setError('Name, slug, and category are required');
                return;
            }

            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('slug', formData.slug);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('category_id', formData.category_id);
            formDataToSend.append('is_active', formData.is_active);

            const response = await subcategoriesAPI.create(formDataToSend);

            if (response.data.status === 'success') {
                setSuccessMessage('Subcategory created successfully!');
                setShowCreateModal(false);
                resetForm();
                fetchSubcategories();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data.message || 'Failed to create subcategory');
            }

        } catch (err) {
            console.error('Error creating subcategory:', err);
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create subcategory');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedSubcategory?.id) {
            setError('Subcategory ID is missing');
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            if (!formData.name || !formData.slug || !formData.category_id) {
                setError('Name, slug, and category are required');
                return;
            }

            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('slug', formData.slug);
            formDataToSend.append('description', formData.description || '');
            formDataToSend.append('category_id', formData.category_id);
            formDataToSend.append('is_active', formData.is_active);

            const response = await subcategoriesAPI.update(selectedSubcategory.id, formDataToSend);

            if (response.data.status === 'success') {
                setSuccessMessage('Subcategory updated successfully!');
                setShowEditModal(false);
                resetForm();
                setSelectedSubcategory(null);
                setIsEditing(false);
                fetchSubcategories();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data.message || 'Failed to update subcategory');
            }

        } catch (err) {
            console.error('Error updating subcategory:', err);
            let errorMsg = 'Failed to update subcategory';
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            }
            setError(errorMsg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedSubcategory?.id) {
            setError('Subcategory ID is missing');
            setShowDeleteModal(false);
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Deleting subcategory with ID:', selectedSubcategory.id);

            const subcategoryId = selectedSubcategory.id;

            // Get the authentication token
            const token = localStorage.getItem('token') || sessionStorage.getItem('token') || 'your-token-here';

            // Make DELETE request with proper headers
            const response = await fetch(`http://localhost:8000/subcategories/${subcategoryId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('Delete response status:', response.status);

            // Handle 204 No Content response (if backend returns that)
            if (response.status === 204) {
                setSuccessMessage('Subcategory deleted successfully!');

                // Remove from state
                setSubcategories(prev => prev.filter(sub => sub.id !== subcategoryId));
                setFilteredSubcategories(prev => prev.filter(sub => sub.id !== subcategoryId));

                // Update pagination
                setPagination(prev => ({
                    ...prev,
                    total: prev.total - 1
                }));

                setShowDeleteModal(false);
                setSelectedSubcategory(null);
                setTimeout(() => setSuccessMessage(''), 3000);
                return;
            }

            // For other status codes, try to parse JSON
            const data = await response.json();
            console.log('Delete response data:', data);

            if (response.ok) {
                setSuccessMessage('Subcategory deleted successfully!');

                // Remove from state
                setSubcategories(prev => prev.filter(sub => sub.id !== subcategoryId));
                setFilteredSubcategories(prev => prev.filter(sub => sub.id !== subcategoryId));

                // Update pagination
                setPagination(prev => ({
                    ...prev,
                    total: prev.total - 1
                }));

                setShowDeleteModal(false);
                setSelectedSubcategory(null);
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                // Handle backend error messages
                if (data && data.message) {
                    setError(data.message);
                } else if (response.status === 404) {
                    setError('Subcategory not found');
                    // Remove from state anyway
                    setSubcategories(prev => prev.filter(sub => sub.id !== subcategoryId));
                    setFilteredSubcategories(prev => prev.filter(sub => sub.id !== subcategoryId));
                } else if (response.status === 403) {
                    setError('Permission denied');
                } else if (response.status === 409) {
                    setError(data.message || 'Cannot delete subcategory with associated products');
                } else {
                    setError('Failed to delete subcategory');
                }
            }

        } catch (err) {
            console.error('Error deleting subcategory:', err);

            // Network error or JSON parsing error
            if (err instanceof SyntaxError) {
                // This might happen if we get a 204 response but try to parse JSON
                setError('Delete operation completed but server returned no content');
            } else {
                setError(err.message || 'Failed to delete subcategory');
            }
        } finally {
            setActionLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleStatusChange = async (subcategoryId, currentIsActive) => {
        try {
            const subcategory = subcategories.find(s => s.id === subcategoryId);
            if (!subcategory) return;

            const newIsActive = subcategory.is_active === 1 ? '0' : '1';

            const formDataToSend = new FormData();
            formDataToSend.append('is_active', newIsActive);

            await subcategoriesAPI.update(subcategoryId, formDataToSend);
            fetchSubcategories();
        } catch (err) {
            console.error('Error changing subcategory status:', err);
            setError('Failed to change subcategory status');
        }
    };

    const openEditModal = (subcategory) => {
        setSelectedSubcategory(subcategory);
        setFormData({
            name: subcategory.name || '',
            slug: subcategory.slug || '',
            description: subcategory.description || '',
            category_id: subcategory.category_id || '',
            is_active: subcategory.is_active?.toString() || '1'
        });
        setIsEditing(true);
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (subcategory) => {
        console.log('Opening delete modal for subcategory:', subcategory);
        console.log('Subcategory ID:', subcategory?.id);
        console.log('Product count:', subcategory?.product_count);

        setSelectedSubcategory(subcategory);
        setShowDeleteModal(true);
        setError('');
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            description: '',
            category_id: '',
            is_active: '1'
        });
        setIsEditing(false);
        setSelectedSubcategory(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const getStatusBadge = (isActive) => {
        const isActiveBool = isActive === 1 || isActive === true || isActive === '1';
        return isActiveBool ?
            <Badge bg="success">Active</Badge> :
            <Badge bg="secondary">Inactive</Badge>;
    };

    const getCategoryName = (categoryId) => {
        if (!categoryId) return 'No Category';
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : `Category ${categoryId.substring(0, 8)}...`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const generateSlugFromName = (name) => {
        if (!name) return '';
        return name
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/--+/g, '-')
            .trim();
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    if (loading || categoriesLoading) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading subcategories...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Subcategories Management</h1>
                    <p className="text-muted mb-0">Manage product subcategories and organization</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-2" />
                        Create Subcategory
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <strong>Error:</strong> {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    <strong>Success:</strong> {successMessage}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search subcategories by name, slug or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                            </div>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small mb-1 txt-cl">Filter by Category</Form.Label>
                                <Form.Select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    size="sm"
                                >
                                    <option value="">All Categories</option>
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{searchTerm ? filteredSubcategories.length : subcategories.length}</strong> subcategories
                                </small>
                            </div>
                            {(searchTerm || selectedCategory) && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedCategory('');
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th width="60"></th>
                                <th>Subcategory</th>
                                <th>Slug</th>
                                <th>Description</th>
                                <th>Category</th>
                                <th>Products</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSubcategories.length > 0 ? (
                                filteredSubcategories.map(subcategory => (
                                    <tr key={subcategory.id}>
                                        <td>
                                            <div
                                                className="bg-info rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '40px', height: '40px' }}
                                            >
                                                <FaFolder className="text-white" size={16} />
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <div className="fw-semibold">{subcategory.name || 'N/A'}</div>
                                                <small className="text-muted">
                                                    ID: {subcategory.id?.substring(0, 12)}...
                                                </small>
                                            </div>
                                        </td>
                                        <td>
                                            <code className="small">{subcategory.slug || 'N/A'}</code>
                                        </td>
                                        <td>
                                            <div className="small text-muted">
                                                {subcategory.description ?
                                                    `${subcategory.description.substring(0, 60)}${subcategory.description.length > 60 ? '...' : ''}`
                                                    : 'No description'}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge bg="primary">
                                                {getCategoryName(subcategory.category_id)}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg="secondary">
                                                {subcategory.product_count || 0}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getStatusBadge(subcategory.is_active)}
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="ms-2 p-0"
                                                    onClick={() => handleStatusChange(subcategory.id, subcategory.is_active)}
                                                    title={`Change to ${subcategory.is_active === 1 ? 'inactive' : 'active'}`}
                                                >
                                                    {subcategory.is_active === 1 ? (
                                                        <FaEye className="text-danger" size={14} />
                                                    ) : (
                                                        <FaEye className="text-success" size={14} />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div>{formatDate(subcategory.created_at)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openEditModal(subcategory)}
                                                    title="Edit Subcategory"
                                                    disabled={actionLoading}
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(subcategory)}
                                                    title="Delete Subcategory"
                                                    disabled={actionLoading}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm || selectedCategory ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No subcategories found matching your filters</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaFolder className="h3 mb-3" />
                                                    <p>No subcategories found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {subcategories.length} of {pagination.total} subcategories
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Stats Summary */}
            {subcategories.length > 0 && (
                <Row className="mt-4 g-3">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-primary text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">{pagination.total}</div>
                                <small>Total Subcategories</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-success text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {subcategories.filter(s => s.is_active === 1).length}
                                </div>
                                <small>Active Subcategories</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-info text-white">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {new Set(subcategories.map(s => s.category_id)).size}
                                </div>
                                <small>Categories with Subcategories</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm bg-warning text-dark">
                            <Card.Body className="text-center py-3">
                                <div className="h4 mb-1">
                                    {subcategories.reduce((total, sub) => total + (sub.product_count || 0), 0)}
                                </div>
                                <small>Total Products</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Subcategory"
                onSubmit={handleCreate}
                submitText={actionLoading ? "Creating..." : "Create Subcategory"}
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Subcategory Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => {
                                    const newName = e.target.value;
                                    setFormData({
                                        ...formData,
                                        name: newName,
                                        slug: formData.slug || generateSlugFromName(newName)
                                    });
                                }}
                                required
                                placeholder="Enter subcategory name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Slug *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                required
                                placeholder="Enter URL slug"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                URL-friendly version of the name (lowercase, hyphens)
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter subcategory description"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category *</Form.Label>
                            <Form.Select
                                value={formData.category_id}
                                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                                required
                                disabled={actionLoading}
                            >
                                <option value="">Select a category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                                disabled={actionLoading}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit Subcategory: ${selectedSubcategory?.name || ''}`}
                onSubmit={handleUpdate}
                submitText={actionLoading ? "Updating..." : "Update Subcategory"}
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Subcategory Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                placeholder="Enter subcategory name"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Slug *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                required
                                placeholder="Enter URL slug"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                URL-friendly version of the name (lowercase, hyphens)
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Enter subcategory description"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Category *</Form.Label>
                            <Form.Select
                                value={formData.category_id}
                                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                                required
                                disabled={actionLoading}
                            >
                                <option value="">Select a category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                                disabled={actionLoading}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Subcategory"
                message={
                    <div>
                        <p>Are you sure you want to delete subcategory <strong>"{selectedSubcategory?.name}"</strong>?</p>
                        <div className="alert alert-warning small">
                            <strong>Note:</strong> You cannot delete a subcategory that has:
                            <ul className="mb-0">
                                <li>Associated products: <strong>{selectedSubcategory?.product_count || 0}</strong></li>
                            </ul>
                        </div>
                        <p className="text-danger mb-0 mt-2">
                            This action cannot be undone.
                        </p>
                        <p className="text-muted small mt-2 mb-0">
                            Subcategory ID: <code>{selectedSubcategory?.id}</code>
                        </p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading || (selectedSubcategory?.product_count > 0)}
                variant="danger"
                loading={actionLoading}
            />
        </div>
    );
};

export default Subcategories;